<template>
  <div class="card-body">
    <LoadingContainer v-if="loading" />

    <EmptyState
      v-else-if="!connectAccounts || connectAccounts.length === 0"
      :msg="'Nenhuma conta Stripe Connect encontrada'"
      :title="'Sem contas'"
    />

    <div v-else>
      <div class="mb-3 text-end">
        <button
          class="btn btn-outline-danger btn-sm"
          @click="showResetModal = true"
        >
          <i class="fa-solid fa-rotate-left me-1"></i> Resetar conta Stripe
        </button>
      </div>

      <div class="row">
        <div class="col-12">
          <div
            v-for="(account, index) in connectAccounts"
            :key="'connect-' + index"
            class="card mb-3 connect-card"
          >
            <!-- Header -->
            <div class="card-header d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center gap-2">
                <span class="badge bg-info">{{ getBusinessTypeLabel(account.business_type) }}</span>
                <span v-if="item && item.stripe_id === account.stripe_id" class="badge bg-success">
                  <i class="fa-solid fa-check me-1"></i>Ativa
                </span>
                <small class="text-muted">{{ account.stripe_id }}</small>
              </div>
              <a
                v-if="account.stripe_id"
                :href="getStripeLink(account.stripe_id)"
                target="_blank"
                class="btn btn-sm btn-outline-primary"
              >
                <i class="fa-brands fa-stripe"></i> Ver no Stripe
              </a>
            </div>

            <div class="card-body">
              <!-- Grid: Dados + Status -->
              <div class="row g-3">
                <!-- Coluna: Dados da Conta -->
                <div class="col-md-6">
                  <div class="info-section">
                    <h6 class="section-title">
                      <i class="fa-solid fa-user me-2"></i>Dados da Conta
                    </h6>
                    <table class="info-table">
                      <tr>
                        <td class="label">Tipo</td>
                        <td>{{ getBusinessTypeLabel(account.business_type) }}</td>
                      </tr>
                      <tr>
                        <td class="label">Nome</td>
                        <td><strong>{{ getAccountName(account) }}</strong></td>
                      </tr>
                      <tr v-if="account.business_type === 'company' && account.company_document_number">
                        <td class="label">CNPJ</td>
                        <td>{{ account.company_document_number }}</td>
                      </tr>
                      <tr>
                        <td class="label">Criado em</td>
                        <td>{{ account.created_at_formatted }}</td>
                      </tr>
                    </table>
                  </div>
                </div>

                <!-- Coluna: Status -->
                <div class="col-md-6">
                  <div class="info-section">
                    <h6 class="section-title">
                      <i class="fa-solid fa-circle-check me-2"></i>Status
                    </h6>
                    <table class="info-table">
                      <tr>
                        <td class="label">Cobranças</td>
                        <td>
                          <span :class="account.charges_enabled ? 'status-ok' : 'status-off'">
                            <i :class="account.charges_enabled ? 'fa-solid fa-check' : 'fa-solid fa-xmark'"></i>
                            {{ account.charges_enabled ? "Habilitado" : "Desabilitado" }}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="label">Repasses</td>
                        <td>
                          <span :class="account.payouts_enabled ? 'status-ok' : 'status-off'">
                            <i :class="account.payouts_enabled ? 'fa-solid fa-check' : 'fa-solid fa-xmark'"></i>
                            {{ account.payouts_enabled ? "Habilitado" : "Desabilitado" }}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="label">Pendências</td>
                        <td>
                          <span v-if="account.currently_due && account.currently_due.length > 0" class="badge bg-warning text-dark">
                            {{ account.currently_due.length }} item(s)
                          </span>
                          <span v-else class="status-ok">
                            <i class="fa-solid fa-check"></i> Nenhuma
                          </span>
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Saldo -->
              <div
                v-if="account.stripe_balance_available_amount !== undefined || account.stripe_balance_pending_amount !== undefined"
                class="info-section mt-3"
              >
                <h6 class="section-title">
                  <i class="fa-solid fa-wallet me-2"></i>Saldo
                </h6>
                <div class="d-flex flex-wrap gap-4 align-items-center">
                  <div v-if="account.stripe_balance_available_amount !== undefined">
                    <small class="text-muted d-block">Disponível</small>
                    <strong :class="getBalanceClass(account.stripe_balance_available_amount)" class="fs-5">{{ formatCurrency(account.stripe_balance_available_amount) }}</strong>
                  </div>
                  <div v-if="account.stripe_balance_pending_amount !== undefined">
                    <small class="text-muted d-block">Pendente</small>
                    <span :class="getBalanceClass(account.stripe_balance_pending_amount)" class="fs-5">{{ formatCurrency(account.stripe_balance_pending_amount) }}</span>
                  </div>
                  <div v-if="account.stripe_balance_updated_at" class="ms-auto text-muted">
                    <small><i class="fa-solid fa-clock me-1"></i>Atualizado: {{ formatDateTime(account.stripe_balance_updated_at) }}</small>
                  </div>
                </div>
              </div>

              <!-- Documentos -->
              <div
                v-if="account.bank_document_front || account.bank_document_back || account.company_document_front"
                class="info-section mt-3"
              >
                <h6 class="section-title">
                  <i class="fa-solid fa-file-lines me-2"></i>Documentos
                </h6>
                <div class="d-flex flex-wrap gap-3">
                  <div v-if="account.bank_document_front" class="doc-item">
                    <small class="text-muted d-block mb-1">Banco (frente)</small>
                    <a :href="getDocumentUrl(account.bank_document_front)" target="_blank" class="doc-link" v-if="isPdf(account.bank_document_front)">
                      <i class="fa-solid fa-file-pdf text-danger fa-2x"></i>
                    </a>
                    <a :href="getDocumentUrl(account.bank_document_front)" target="_blank" v-else>
                      <img :src="getDocumentUrl(account.bank_document_front)" class="doc-preview" />
                    </a>
                  </div>
                  <div v-if="account.bank_document_back" class="doc-item">
                    <small class="text-muted d-block mb-1">Banco (verso)</small>
                    <a :href="getDocumentUrl(account.bank_document_back)" target="_blank" class="doc-link" v-if="isPdf(account.bank_document_back)">
                      <i class="fa-solid fa-file-pdf text-danger fa-2x"></i>
                    </a>
                    <a :href="getDocumentUrl(account.bank_document_back)" target="_blank" v-else>
                      <img :src="getDocumentUrl(account.bank_document_back)" class="doc-preview" />
                    </a>
                  </div>
                  <div v-if="account.company_document_front" class="doc-item">
                    <small class="text-muted d-block mb-1">Empresa</small>
                    <a :href="getDocumentUrl(account.company_document_front)" target="_blank" class="doc-link" v-if="isPdf(account.company_document_front)">
                      <i class="fa-solid fa-file-pdf text-danger fa-2x"></i>
                    </a>
                    <a :href="getDocumentUrl(account.company_document_front)" target="_blank" v-else>
                      <img :src="getDocumentUrl(account.company_document_front)" class="doc-preview" />
                    </a>
                  </div>
                </div>
              </div>

              <!-- Alertas -->
              <div
                v-if="(account.last_payout_failed && account.last_failed_payout) || (account.currently_due && account.currently_due.length > 0)"
                class="alerts-section mt-3"
              >
                <!-- Repasse Falhou -->
                <div v-if="account.last_payout_failed && account.last_failed_payout" class="alert-box alert-danger mb-2">
                  <div class="alert-header">
                    <i class="fa-solid fa-circle-xmark"></i>
                    <strong>Último Repasse Falhou</strong>
                    <a
                      v-if="account.last_failed_payout.stripe_payout_id"
                      :href="getPayoutStripeLink(account.stripe_id, account.last_failed_payout.stripe_payout_id)"
                      target="_blank"
                      class="btn btn-sm btn-outline-secondary ms-auto"
                    >
                      <i class="fa-brands fa-stripe me-1"></i> Ver no Stripe
                    </a>
                  </div>
                  <div class="alert-content">
                    <p v-if="account.last_failed_payout.failure_code" class="mb-1">
                      <strong>Motivo:</strong> {{ getFailureMessageTranslated(account.last_failed_payout.failure_code) }}
                    </p>
                    <small class="text-muted">
                      <span v-if="account.last_failed_payout.amount">Valor: {{ formatCurrency(account.last_failed_payout.amount) }}</span>
                      <span v-if="account.last_failed_payout.amount && account.last_failed_payout.created_at"> · </span>
                      <span v-if="account.last_failed_payout.created_at">Data: {{ formatDateTime(account.last_failed_payout.created_at) }}</span>
                    </small>
                  </div>
                </div>

                <!-- Pendências -->
                <div v-if="account.currently_due && account.currently_due.length > 0" class="alert-box alert-warning">
                  <div class="alert-header">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <strong>Pendências</strong>
                    <span class="badge bg-warning text-dark ms-2">{{ account.currently_due.length }}</span>
                  </div>
                  <div class="alert-content">
                    <ul class="pendencias-list">
                      <li v-for="(pendencia, idx) in account.currently_due" :key="'due-' + idx">
                        {{ pendencia }}
                      </li>
                    </ul>
                    <div v-if="pendenciasLink" class="mt-2 pt-2 border-top">
                      <div class="input-group input-group-sm">
                        <span class="input-group-text"><i class="fa-solid fa-link"></i></span>
                        <input type="text" class="form-control bg-light" :value="pendenciasLink" readonly />
                        <button class="btn btn-primary" @click="copyPendenciasLink" title="Copiar link">
                          <i class="fa-solid fa-copy"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Reset Stripe -->
    <div
      class="modal fade"
      :class="{ show: showResetModal }"
      :style="{ display: showResetModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title">
              <i class="fa-solid fa-triangle-exclamation me-2"></i>
              Resetar conta Stripe Connect
            </h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              @click="closeResetModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              <strong>Atenção!</strong> Esta ação irá:
            </div>
            <ul class="mb-3">
              <li>A conta do PsiApp do psicólogo <strong>continua normal</strong></li>
              <li>Somente o cadastro na <strong>plataforma de pagamento</strong> (Stripe) será resetado</li>
              <li>O psicólogo será <strong>deslogado</strong> do aplicativo</li>
              <li>Será necessário pedir para ele <strong>logar novamente</strong> com o mesmo email e senha</li>
              <li>Ao logar, ele precisará criar um <strong>novo cadastro</strong> na plataforma de pagamento</li>
            </ul>
            <div class="form-check mt-4">
              <input
                class="form-check-input"
                type="checkbox"
                id="confirmResetCheckbox"
                v-model="confirmReset"
              />
              <label class="form-check-label" for="confirmResetCheckbox">
                <strong>Eu li e entendi</strong> as consequências desta ação
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeResetModal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-danger"
              :disabled="!confirmReset || resetting"
              @click="handleResetStripe"
            >
              <span v-if="resetting" class="spinner-border spinner-border-sm me-1"></span>
              Confirmar Reset
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showResetModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
export default {
  name: "PsiDetailConnectAccounts",
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      connectAccounts: [],
      showResetModal: false,
      confirmReset: false,
      resetting: false,
      pendenciasLink: null,
    };
  },
  methods: {
    async loadConnectAccounts() {
      if (!this.item?.id) return;

      this.loading = true;
      this.pendenciasLink = null;
      try {
        const response = await this.$connectAccountService.list({
          user_id: this.item.id,
        });

        this.connectAccounts = response.data.map((account) => {
          let currentlyDue = account.currently_due_ptbr;
          if (typeof currentlyDue === 'string' && currentlyDue.length > 0) {
            currentlyDue = currentlyDue.split('|');
          } else if (!Array.isArray(currentlyDue)) {
            currentlyDue = [];
          }

          return {
            ...account,
            currently_due: currentlyDue,
            created_at_formatted: account.created_at
              ? this.$moment(account.created_at).format("DD/MM/YYYY HH:mm")
              : "-",
          };
        });

        // Busca link de pendências se houver contas com pendências
        const hasPendencias = this.connectAccounts.some(
          (acc) => acc.currently_due && acc.currently_due.length > 0
        );
        if (hasPendencias) {
          this.fetchPendenciasLink();
        }
      } catch (error) {
        console.error("Erro ao carregar contas Stripe Connect:", error);
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: error.message || "Erro ao carregar contas Stripe Connect",
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchPendenciasLink() {
      try {
        const res = await this.$connectAccountService.get(
          `api/connect-accounts/pendencias-link`,
          { user_id: this.item.id }
        );
        this.pendenciasLink = res.data?.links?.pending_issues || null;
      } catch (error) {
        console.error("Erro ao buscar link de pendências:", error);
      }
    },
    copyPendenciasLink() {
      navigator.clipboard.writeText(this.pendenciasLink);
      this.$eventBus.$emit("showToast", {
        title: "Copiado",
        msg: "Link copiado para a área de transferência",
      });
    },
    getStatusClass(enabled) {
      return enabled ? "badge bg-success" : "badge bg-secondary";
    },
    getStripeLink(accountId) {
      const baseUrl =
        process.env.VUE_APP_ENV === "prod"
          ? "https://dashboard.stripe.com"
          : "https://dashboard.stripe.com/test";
      return `${baseUrl}/connect/accounts/${accountId}`;
    },
    getPayoutStripeLink(connectAccountId, payoutId) {
      const platformAcctId = 'acct_1Mo623D1MBNGVeyV';
      const urlMid = process.env.VUE_APP_ENV === 'prod' ? 'payouts' : 'test/payouts';
      return `https://dashboard.stripe.com/${platformAcctId}/connect/view-as/${connectAccountId}/${urlMid}/${payoutId}`;
    },
    getBusinessTypeLabel(type) {
      if (type === "individual") return "Pessoa Física";
      if (type === "company") return "Empresa";
      return type;
    },
    getAccountName(account) {
      if (account.business_type === "individual") {
        return (
          `${account.individual_first_name || ""} ${
            account.individual_last_name || ""
          }`.trim() || "-"
        );
      }
      if (account.business_type === "company") {
        return account.company_name || "-";
      }
      return "-";
    },
    isPdf(url) {
      if (!url) return false;
      return url.toLowerCase().endsWith('.pdf');
    },
    getDocumentUrl(path) {
      if (!path) return '';
      return this.$baseImageUrl + path;
    },
    formatCurrency(value) {
      const num = parseFloat(value);
      if (isNaN(num)) return '-';
      return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    getBalanceClass(value) {
      const num = parseFloat(value);
      if (isNaN(num) || num === 0) return 'text-muted';
      if (num < 0) return 'text-danger';
      return 'text-success';
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      return this.$moment(dateStr).format('DD/MM/YYYY HH:mm');
    },
    getFailureMessageTranslated(failureCode) {
      if (!failureCode) return '-';
      const translated = this.$userService.payoutService.getFailureMessage(failureCode);
      return translated || failureCode;
    },
    closeResetModal() {
      this.showResetModal = false;
      this.confirmReset = false;
    },
    async handleResetStripe() {
      if (!this.item?.id) return;

      this.resetting = true;
      try {
        await this.$userService.network.post(
          `api/users/stripe-connect-reset/${this.item.id}`
        );

        this.$eventBus.$emit("showToast", {
          title: "Sucesso",
          msg: "Conta Stripe Connect resetada com sucesso. O psicólogo foi deslogado.",
        });

        this.closeResetModal();
        this.loadConnectAccounts();
      } catch (error) {
        console.error("Erro ao resetar conta Stripe:", error);
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: error.message || "Erro ao resetar conta Stripe Connect",
        });
      } finally {
        this.resetting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Card principal */
.connect-card {
  border: 1px solid #e0e0e0;
}

/* Seções de informação */
.info-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Tabela de informações */
.info-table {
  width: 100%;
  font-size: 0.9rem;
}

.info-table tr {
  border-bottom: 1px solid #e9ecef;
}

.info-table tr:last-child {
  border-bottom: none;
}

.info-table td {
  padding: 8px 0;
}

.info-table td.label {
  color: #6c757d;
  width: 100px;
}

/* Status badges */
.status-ok {
  color: #198754;
  font-weight: 500;
}

.status-ok i {
  margin-right: 4px;
}

.status-off {
  color: #6c757d;
}

.status-off i {
  margin-right: 4px;
}

/* Alertas */
.alerts-section {
  border-top: 1px solid #e9ecef;
  padding-top: 16px;
}

.alert-box {
  border-radius: 8px;
  padding: 16px;
}

.alert-box.alert-danger {
  background-color: #fff5f5;
  border-left: 4px solid #dc3545;
}

.alert-box.alert-warning {
  background-color: #fffdf5;
  border-left: 4px solid #ffc107;
}

.alert-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.alert-box.alert-danger .alert-header i {
  color: #dc3545;
}

.alert-box.alert-warning .alert-header i {
  color: #ffc107;
}

.alert-content {
  padding-left: 24px;
}

/* Lista de pendências */
.pendencias-list {
  padding-left: 20px;
  margin: 0;
  columns: 2;
  column-gap: 32px;
}

.pendencias-list li {
  color: #6c757d;
  font-size: 0.875rem;
  padding: 4px 0;
  break-inside: avoid;
}

/* Documentos */
.doc-item {
  text-align: center;
}

.doc-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.15s;
}

.doc-link:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
}

.doc-preview {
  max-width: 120px;
  max-height: 80px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  object-fit: cover;
  transition: transform 0.15s, box-shadow 0.15s;
}

.doc-preview:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
